import { Injectable } from '@angular/core'
import { AuctionFacade } from 'libs/gb-front/auction/data-access/src'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { catchError, distinctUntilChanged, filter, map, mergeMap, of, switchMap, tap, withLatestFrom } from 'rxjs'
import { CategoriesApiService } from './categories-api.service'
import * as CategoriesActions from './categories.actions'
import { CategoriesFacade } from './categories.facade'
import { GbLoaderService, LoaderActionTypeEnum } from 'libs/shared/utils/loader/src'
import { Action } from '@ngrx/store'
import { GbItemsObserverService } from 'libs/shared/ui/src/lib/gb-items-observer/gb-items-observer.service'

@Injectable()
export class CategoriesEffects {
  loadCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoriesActions.loadCategories),
      withLatestFrom(this.auctionFacade.auctionUrlName$, this.categoriesFacade.allCategories$),
      tap(() => this.loaderService.setLoading(LoaderActionTypeEnum.LoadCategories)),
      switchMap(([, urlName, categories]) => {
        let offset = 0
        if (categories) {
          offset = categories.length - offset
        }
        return this.categoriesApiService.getCategories(urlName, offset).pipe(
          mergeMap((response) => {
            return [
              CategoriesActions.loadCategoriesSuccess({ categories: response.results }),
              CategoriesActions.updateCount({ count: response.count }),
            ]
          }),
          catchError((error) => of(CategoriesActions.loadCategoriesFailure({ error }))),
        )
      }),
    ),
  )

  syncCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoriesActions.syncCategory, CategoriesActions.updateSelectedCategoryUuid),
      withLatestFrom(this.auctionFacade.auctionUrlName$),
      switchMap(([action, urlName]) => {
        return this.categoriesApiService.syncCategory(urlName, action.uuid).pipe(
          withLatestFrom(this.categoriesFacade.allCategories$),
          map(([response, categories]) => {
            if (categories.some((category) => category.uuid === response.uuid)) {
              return CategoriesActions.updateCategory({
                update: {
                  id: response.uuid,
                  changes: response,
                },
              })
            } else {
              return CategoriesActions.addCategory({
                category: response,
              })
            }
          }),
          catchError((error) =>
            of(
              CategoriesActions.resyncCategoryError({
                error,
                uuid: action.uuid,
              }),
            ),
          ),
        )
      }),
    ),
  )

  resyncCategoryError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoriesActions.resyncCategoryError),
      withLatestFrom(this.categoriesFacade.allCategories$),
      switchMap(([action, categories]) => {
        const actions: Action[] = []

        if (
          action.error.status &&
          action.error.status === 404 &&
          action.uuid &&
          categories.some((category) => category.uuid === action.uuid)
        ) {
          actions.push(CategoriesActions.deleteCategory({ uuid: action.uuid }))
        }
        return actions
      }),
    ),
  )

  setLoaderComplete$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          CategoriesActions.loadCategoriesSuccess,
          CategoriesActions.loadCategoriesFailure,
          CategoriesActions.resyncCategoriesSuccess,
          CategoriesActions.resyncCategoriesError,
        ),
        tap(() => this.loaderService.setLoadingComplete(LoaderActionTypeEnum.LoadCategories)),
      ),
    { dispatch: false },
  )

  resyncCategories$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoriesActions.resyncCategories),
      withLatestFrom(this.auctionFacade.auctionUrlName$),
      mergeMap(([action, urlName]) =>
        this.categoriesApiService.getCategories(urlName, action.offset).pipe(
          switchMap((response) => [
            CategoriesActions.resyncCategoriesSuccess({
              categories: response.results,
              offset: action.offset,
              count: response.count,
            }),
          ]),
          catchError((error) => of(CategoriesActions.resyncCategoriesError({ error }))),
        ),
      ),
    ),
  )

  resyncCategoriesOnScroll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoriesActions.resyncCategoriesOnScroll),
      distinctUntilChanged((prev, curr) => prev.offset === curr.offset),
      withLatestFrom(this.categoriesFacade.stalePages$),
      filter(([action, stalePages]) => stalePages.includes(action.offset)),
      map(([action]) => CategoriesActions.resyncCategories(action.offset)),
    ),
  )

  resyncCategoriesCurrentPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoriesActions.resyncCategoriesCurrentPage),
      withLatestFrom(this.itemsObserverService.getPageInView()),
      map(([, offset]) => CategoriesActions.resyncCategories(offset)),
    ),
  )

  resyncCategoriesSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoriesActions.resyncCategoriesSuccess),
      switchMap((action) => [
        CategoriesActions.removePageFromStale({
          page: action.offset,
        }),
        CategoriesActions.updateCount({
          count: action.count,
        }),
      ]),
    ),
  )

  categoryItemPreviewUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoriesActions.categoryItemPreviewUpdate),
      withLatestFrom(this.categoriesFacade.allCategories$),
      mergeMap(([action, categories]) => {
        const actions: Action[] = []
        categories.forEach((category) => {
          if (action.item.categories.includes(category.uuid)) {
            const previewItems = [...category.items_preview]
            const index = previewItems.findIndex((item) => item.uuid === action.item.uuid)

            if (index >= 0) {
              previewItems[index] = action.item

              actions.push(
                CategoriesActions.updateCategory({
                  update: {
                    id: category.uuid,
                    changes: { ...category, items_preview: previewItems },
                  },
                }),
              )
            }
          }
        })
        return actions
      }),
    ),
  )

  categoryItemPreviewDeleteItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CategoriesActions.categoryItemPreviewDeleteItem),
      withLatestFrom(this.categoriesFacade.allCategories$),
      mergeMap(([action, categories]) => {
        const actions: Action[] = []
        categories.forEach((category) => {
          if (category.items.includes(action.itemUuid)) {
            const previewItems = [...category.items_preview]
            const index = previewItems.findIndex((item) => item.uuid === action.itemUuid)

            if (index >= 0) {
              previewItems.splice(index, 1)

              actions.push(
                CategoriesActions.updateCategory({
                  update: {
                    id: category.uuid,
                    changes: { ...category, items_preview: previewItems },
                  },
                }),
              )
            }
          }
        })
        return actions
      }),
    ),
  )

  constructor(
    private readonly actions$: Actions,
    private auctionFacade: AuctionFacade,
    private categoriesFacade: CategoriesFacade,
    private categoriesApiService: CategoriesApiService,
    private loaderService: GbLoaderService,
    private itemsObserverService: GbItemsObserverService,
  ) {}
}
